import { useTranslate } from 'module/hooks/use-translation';
import { Button } from 'module/ui/components/atoms/button/button';
import { CustomLayoutDrawerContent } from 'module/ui/components/atoms/drawer';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { FormInstance, InputRef } from 'antd';
import { useRef, useEffect, FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { PermissionsSelect } from './permissions-select';
import { AddKnowledgeTriggerType } from '../add-knowledge-trigger';
import { ContentTypeSelector } from './content-type-selector';

export interface FolderStepFormValues {
  folderName: string;
  permissions?: {
    userGroupIds: number[];
    permissionSetIds: number[];
  };
  contentType: AddKnowledgeTriggerType;
}

interface Props {
  form: FormInstance<FolderStepFormValues>;
  isFormValid: boolean;
  onFormValuesChange: (values: FolderStepFormValues) => void;
  onClose: () => void;
  onNext: () => void;
}

export const FolderCreateStep: FC<Props> = ({
  form,
  onFormValuesChange,
  onClose,
  onNext,
  isFormValid,
}) => {
  const translate = useTranslate();
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title>
        <FormattedMessage id="knowledge_base.folder.create.title" />
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={onFormValuesChange}
          initialValues={{ contentType: AddKnowledgeTriggerType.AiPages }}
        >
          <Form.Item
            name="folderName"
            label={
              <FormattedMessage id="knowledge_base.folder.create.folder_name.label" />
            }
            rules={[
              {
                required: true,
                message: translate('input.required'),
              },
            ]}
            requiredMark={'optional'}
          >
            <Input
              ref={inputRef}
              placeholder={translate(
                'knowledge_base.folder.create.folder_name.placeholder',
              )}
              size="middle"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="permissions"
            label={
              <FormattedMessage id="knowledge_base.folder.create.permissions.label" />
            }
            extra={
              <FormattedMessage id="knowledge_base.folder.create.permissions.description" />
            }
            hidden
          >
            <PermissionsSelect />
          </Form.Item>
          <Form.Item name="contentType">
            <ContentTypeSelector />
          </Form.Item>
        </Form>
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button type="secondary" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button type="primary" onClick={onNext} disabled={!isFormValid}>
          <FormattedMessage id="Next" />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
